import { Link, useParams } from "react-router-dom";
import { ChevronRight, ArrowUpRight } from "lucide-react";
import CtaBand from "@/components/CtaBand";
import { Reveal } from "@/components/motion";
import { insights } from "@/data/insights";
import { usePageMeta } from "@/lib/api";
import NotFoundPage from "./NotFoundPage";

export default function InsightDetailPage() {
  const { slug } = useParams();
  const article = insights.find((a) => a.slug === slug);
  usePageMeta(
    article ? `${article.title} | SarvaRitam Insights` : "Insight not found | SarvaRitam",
    article?.excerpt
  );
  if (!article) return <NotFoundPage />;
  const related = insights.filter((a) => a.slug !== slug && a.category === article.category).slice(0, 2);

  return (
    <>
      <section className="bg-forest text-offwhite relative grain overflow-hidden pt-40 pb-16 lg:pt-48" data-testid="insight-hero">
        <div className="absolute inset-0 pointer-events-none opacity-30" style={{ background: "radial-gradient(55% 70% at 80% 0%, rgba(198,161,91,0.45) 0%, transparent 65%)" }} />
        <div className="wrap relative max-w-3xl">
          <Reveal>
            <nav className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest2 text-white/70 mb-8" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-gold">Home</Link> <ChevronRight size={11} />
              <Link to="/insights" className="hover:text-gold">Insights</Link> <ChevronRight size={11} />
              <span className="text-gold">{article.category}</span>
            </nav>
          </Reveal>
          <Reveal delay={0.05}>
            <p className="eyebrow text-gold mb-5">{article.type} · {article.minutes} min read</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="h-display font-semibold text-[30px] sm:text-4xl lg:text-[52px] leading-[1.08]">{article.title}</h1>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-6 text-white/90 text-lg leading-relaxed">{article.excerpt}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-offwhite text-charcoal sec">
        <div className="wrap max-w-3xl">
          {article.body.map((p, i) => (
            <Reveal key={i} delay={0.02 * i}>
              <p className={`text-[16.5px] leading-[1.85] text-charcoal/80 ${i === 0 ? "first-letter:font-display first-letter:text-[52px] first-letter:font-bold first-letter:text-forest first-letter:float-left first-letter:mr-3 first-letter:leading-[0.85]" : ""} mb-8`}>
                {p}
              </p>
            </Reveal>
          ))}
          <Reveal>
            <div className="border-l-2 border-gold pl-6 py-2 my-12">
              <p className="font-display font-semibold text-xl">Working on this topic?</p>
              <p className="text-[14px] text-charcoal/60 mt-2">SarvaRitam advises corporates, investors and institutions across ESG strategy, reporting and responsible investment.</p>
              <Link to="/contact" className="btn-forest mt-6" data-testid="insight-detail-cta">
                Book an ESG Consultation <ArrowUpRight size={14} />
              </Link>
            </div>
          </Reveal>
          {related.length > 0 && (
            <div className="mt-8">
              <p className="eyebrow text-forest mb-7">Related insights</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {related.map((r) => (
                  <Link key={r.slug} to={`/insights/${r.slug}`} className="lift border rule-light bg-white/60 hover:border-forest p-7 rounded-sm" data-testid={`related-insight-${r.slug}`}>
                    <p className="font-mono text-[10.5px] uppercase tracking-widest2 text-sage">{r.type} · {r.category}</p>
                    <h3 className="font-display font-semibold text-[17px] mt-3 leading-snug">{r.title}</h3>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
      <CtaBand />
    </>
  );
}
